/**
 * Local Database Provider (SQLite in Browser)
 * Implementation for free tier users - stores data in browser using SQLite + IndexedDB
 */

import type { StorageProvider } from "./StorageProvider";
import { initDB, isDBInitialized, query, execute, saveDB, exportDatabaseFile, importDatabaseFile } from '../db';
import * as txnOps from './operations/transactionOperations';
import * as ruleOps from './operations/ruleOperations';
import * as budgetOps from './operations/budgetOperations';
import * as categoryOps from './operations/categoryOperations';
import * as categoryAliasOps from './operations/categoryAliasOperations';
import * as recurringOps from './operations/recurringOperations';
import * as userActionOps from './operations/userActionOperations';

/**
 * Create Local Database Provider using SQLite in browser
 * Factory function that returns a StorageProvider implementation for free tier users
 */
export function createLocalDBProvider(): StorageProvider {
  return {
    type: "local",

    // Initialization
    initialize: async () => await initDB(),
    isInitialized: () => isDBInitialized(),

    // ==================== TRANSACTIONS ====================
    getTransactions: async () => await txnOps.getAll(),
    saveTransactionsForFile: async (csvFilename, transactions) => await txnOps.saveForFile(csvFilename, transactions),
    upsertTransaction: async (txn) => await txnOps.upsert(txn),
    bulkUpdateTransactions: async (description, updates) => await txnOps.bulkUpdateByDescription(description, updates),
    undoBulkUpdate: async (previousStates) => await txnOps.undoBulkUpdate(previousStates),
    splitTransaction: async (transactionId, splits) => await txnOps.split(transactionId, splits),
    unsplitTransaction: async (transactionId) => await txnOps.unsplit(transactionId),
    deleteAllTransactions: async () => await txnOps.deleteAll(),
    deleteTransactionsFromFile: async (filename) => await txnOps.deleteFromFile(filename),
    getReviewableTransactions: async () => await txnOps.getReviewable(),

    // ==================== PROCESSED FILES ====================
    getProcessedFiles: async () => {
      try {
        const rows = query<any>("SELECT * FROM processed_files ORDER BY processed_at DESC");
        return rows.map((row) => {
          // Get date range for this file's transactions
          let dateRange: { start: string; end: string } | undefined;
          try {
            const dateRangeRows = query<{ min_date: string; max_date: string }>(
              `SELECT MIN(date) as min_date, MAX(date) as max_date
               FROM transactions
               WHERE file_id = ?`,
              [row.filename]
            );
            if (dateRangeRows.length > 0 && dateRangeRows[0].min_date) {
              dateRange = {
                start: dateRangeRows[0].min_date,
                end: dateRangeRows[0].max_date
              };
            }
          } catch (dateErr) {
            console.warn(`Error fetching date range for ${row.filename}:`, dateErr);
          }

          return {
            filename: row.filename,
            transactionCount: row.transaction_count,
            processedAt: row.processed_at,
            fileHash: row.file_hash,
            bank: row.bank,
            person: row.person,
            accountName: row.account_name,
            accountType: row.account_type,
            dateRange
          };
        });
      } catch (error) {
        console.error("Error loading processed files:", error);
        return [];
      }
    },

    markFileAsProcessed: async (filename: string, transactionCount: number, fileHash?: string, metadata?: { bank?: string; person?: string; accountName?: string; accountType?: string }) => {
      try {
        execute(
          `INSERT OR REPLACE INTO processed_files (filename, transaction_count, processed_at, file_hash, bank, person, account_name, account_type)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            filename,
            transactionCount,
            new Date().toISOString(),
            fileHash || null,
            metadata?.bank || null,
            metadata?.person || null,
            metadata?.accountName || null,
            metadata?.accountType || null
          ]
        );
        await saveDB();
        return true;
      } catch (error) {
        console.error("Error marking file as processed:", error);
        return false;
      }
    },

    isFileProcessed: async (filename: string) => {
      try {
        const rows = query<any>(
          "SELECT 1 FROM processed_files WHERE filename = ? LIMIT 1",
          [filename]
        );
        return rows.length > 0;
      } catch (error) {
        console.error("Error checking if file is processed:", error);
        return false;
      }
    },

    isFileHashProcessed: async (fileHash: string) => {
      try {
        const rows = query<any>(
          "SELECT filename FROM processed_files WHERE file_hash = ? LIMIT 1",
          [fileHash]
        );
        if (rows.length > 0) {
          return { processed: true, filename: rows[0].filename };
        }
        return { processed: false };
      } catch (error) {
        console.error("Error checking if file hash is processed:", error);
        return { processed: false };
      }
    },

    deleteProcessedFile: async (filename: string) => {
      try {
        // First delete all transactions from this file
        const deleteResult = await txnOps.deleteFromFile(filename);

        if (!deleteResult.success) {
          return {
            success: false,
            deletedTransactions: 0,
            error: deleteResult.error || 'Failed to delete transactions'
          };
        }

        // Then delete the processed file record
        execute("DELETE FROM processed_files WHERE filename = ?", [filename]);
        await saveDB();

        return {
          success: true,
          deletedTransactions: deleteResult.deletedCount
        };
      } catch (error) {
        console.error("Error deleting processed file:", error);
        return {
          success: false,
          deletedTransactions: 0,
          error: error instanceof Error ? error.message : 'Failed to delete file'
        };
      }
    },

    getInstitutions: async () => {
      try {
        const rows = query<any>(
          'SELECT DISTINCT bank FROM transactions WHERE bank IS NOT NULL AND bank != "" ORDER BY bank',
        );
        return rows.map((row) => row.bank);
      } catch (error) {
        console.error("Error loading institutions:", error);
        return [];
      }
    },

    getPeople: async () => {
      try {
        const rows = query<any>(
          'SELECT DISTINCT person FROM transactions WHERE person IS NOT NULL AND person != "" ORDER BY person',
        );
        return rows.map((row) => row.person);
      } catch (error) {
        console.error("Error loading people:", error);
        return [];
      }
    },

    // ==================== CATEGORIES ====================
    getCategories: async () => await categoryOps.getAll(),
    addCategory: async (category) => await categoryOps.add(category),
    updateCategory: async (oldName, newName) => await categoryOps.update(oldName, newName),
    deleteCategory: async (name, replacementCategory) => await categoryOps.deleteCategory(name, replacementCategory),

    // ==================== CATEGORY ALIASES ====================
    resolveCategoryAlias: async (categoryName) => await categoryAliasOps.resolveCategoryAlias(categoryName),
    getCategoryAliases: async () => await categoryAliasOps.getAllAliases(),

    // ==================== RULES ====================
    getRules: async () => await ruleOps.getAll(),
    createRulesFromReview: async (decisions) => await ruleOps.createFromReview(decisions),
    createRule: async (pattern, category, person = null, options = {}) =>
      await ruleOps.create(pattern, category, person, options),
    updateRule: async (ruleId, updates) => await ruleOps.update(ruleId, updates),
    deleteRule: async (ruleId) => await ruleOps.deleteRule(ruleId),
    deleteAllRules: async () => await ruleOps.deleteAll(),
    applyRuleToExisting: async (ruleId, forceApply = false) => await ruleOps.applyToExisting(ruleId, forceApply),
    checkRuleExists: async (pattern) => await ruleOps.checkExists(pattern),
    applyRulesToTransaction: async (description) => await ruleOps.applyToTransaction(description),

    // ==================== DATA MANAGEMENT ====================
    exportData: async () => await exportDatabaseFile(),
    importData: async (data) => {
      const file = new File([data], "import.db");
      await importDatabaseFile(file);
    },

    // ==================== BUDGETS ====================
    getBudgets: async () => await budgetOps.getAll(),
    createBudget: async (data) => await budgetOps.create(data),
    updateBudget: async (name, type, updates) => await budgetOps.update(name, type, updates),
    deleteBudget: async (name, type) => await budgetOps.deleteBudget(name, type),
    deleteAllBudgets: async () => await budgetOps.deleteAll(),
    getBudgetStatus: async (month) => {
      const transactions = await txnOps.getAll();
      return await budgetOps.getStatus(month, transactions);
    },
    updateBudgetRollover: async (name, type, rolloverAmount) =>
      await budgetOps.updateRollover(name, type, rolloverAmount),
    resetBudgetRollover: async (name, type) => await budgetOps.resetRollover(name, type),
    getBudgetSettings: async () => await budgetOps.getSettings(),
    updateBudgetSettings: async (settings) => await budgetOps.updateSettings(settings),

    // ==================== RECURRING PATTERN DISMISSALS ====================
    dismissRecurringPattern: async (pattern) => await recurringOps.dismissPattern(pattern),
    getDismissedPatterns: async () => await recurringOps.getDismissedPatterns(),
    undismissRecurringPattern: async (pattern) => await recurringOps.undismissPattern(pattern),
    snoozeRecurringPattern: async (pattern, days) => await recurringOps.snoozePattern(pattern, days),
    getSnoozedPatterns: async () => await recurringOps.getSnoozedPatterns(),
    updateRecurringInterval: async (pattern, customInterval) =>
      await recurringOps.updateInterval(pattern, customInterval),
    getRecurringMetadata: async (pattern) => await recurringOps.getMetadata(pattern),

    // ==================== USER ACTIONS (PREFERENCES, DISMISSALS, UI STATE) ====================
    // Preferences
    setUserPreference: async (key, value) => await userActionOps.setUserPreference(key, value),
    getUserPreference: (key) => userActionOps.getUserPreference(key),
    getAllUserPreferences: () => userActionOps.getAllUserPreferences(),
    deleteUserPreference: async (key) => await userActionOps.deleteUserPreference(key),

    // Dismissals
    dismissItem: async (key, days) => await userActionOps.dismissItem(key, days),
    isDismissed: (key) => userActionOps.isDismissed(key),
    getDismissedItems: () => userActionOps.getDismissedItems(),
    getDismissedItemsWithExpiry: () => userActionOps.getDismissedItemsWithExpiry(),
    undismissItem: async (key) => await userActionOps.undismissItem(key),

    // UI State
    setUIState: async (key, value) => await userActionOps.setUIState(key, value),
    getUIState: (key) => userActionOps.getUIState(key),
    getAllUIState: () => userActionOps.getAllUIState(),
    deleteUIState: async (key) => await userActionOps.deleteUIState(key),
    clearAllUIState: async () => await userActionOps.clearAllUIState(),

    // Cleanup
    clearExpiredActions: async () => await userActionOps.clearExpiredActions(),
  };
}
